import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Check, X, Trash2, Eye, MessageCircle, Shield, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { nohellocian } from '../../assets/assets';
import { useGetHellocians, useToggleBlockHellocian, useDeleteHellocian, useGetTrashedHellocians, useRestoreHellocian, useHardDeleteHellocian, useBulkDeleteHellocians } from '../../services/auth/auth';
import { useAlert } from '../../context/AlertContext';
import { toast } from 'react-toastify';

const HellociansTable = () => {
    const [view, setView] = useState('active'); // 'active' | 'trash'
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { mutateAsync: fetchActive } = useGetHellocians();
    const { mutateAsync: fetchTrashed } = useGetTrashedHellocians();
    const { mutateAsync: toggleBlock } = useToggleBlockHellocian();
    const { mutateAsync: deleteHellocian } = useDeleteHellocian();
    const { mutateAsync: restoreHellocian } = useRestoreHellocian();
    const { mutateAsync: hardDeleteHellocian } = useHardDeleteHellocian();
    const { mutateAsync: bulkDelete } = useBulkDeleteHellocians();
    const setAlert = useAlert();

    const loadHellocians = async () => {
        setIsLoading(true);
        try {
            const response = await (view === 'active' ? fetchActive() : fetchTrashed());
            setUsers(response.hellocians.map(u => ({ ...u, selected: false })));
        } catch (error) {
            toast.error('Failed to load hellocians');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadHellocians();
    }, [view]);

    const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
    const [showRowDropdown, setShowRowDropdown] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [blockReason, setBlockReason] = useState("");
    const [isActioning, setIsActioning] = useState(false);

    const toggleUserSelection = (userId) => {
        setUsers(users.map(user =>
            user._id === userId ? { ...user, selected: !user.selected } : user
        ));
    };

    const selectAllUsers = () => {
        const allSelected = users.every(user => user.selected);
        setUsers(users.map(user => ({ ...user, selected: !allSelected })));
    };

    const handleDelete = async (userId) => {
        try {
            setIsActioning(true);
            if (view === 'active') {
                await deleteHellocian(userId);
                toast.success("Hellocian moved to trash");
            } else {
                await hardDeleteHellocian(userId);
                toast.success("Hellocian permanently deleted");
            }
            setUsers(users.filter(user => user._id !== userId));
            closeModal();
        } catch (error) {
            toast.error("Failed to delete hellocian");
        } finally {
            setIsActioning(false);
        }
    };

    const handleRestore = async (userId) => {
        try {
            setIsActioning(true);
            await restoreHellocian(userId);
            toast.success("Hellocian restored");
            setUsers(users.filter(user => user._id !== userId));
            closeModal();
        } catch (error) {
            toast.error("Failed to restore hellocian");
        } finally {
            setIsActioning(false);
        }
    };

    const handleBulkDelete = async () => {
        const selectedIds = users.filter(u => u.selected).map(u => u._id);
        if (selectedIds.length === 0) return;
        
        try {
            setIsActioning(true);
            await bulkDelete({ ids: selectedIds, action: view === 'active' ? 'soft' : 'hard' });
            toast.success(`${selectedIds.length} users ${view === 'active' ? 'moved to trash' : 'permanently deleted'}`);
            setUsers(users.filter(u => !u.selected));
            setShowHeaderDropdown(false);
        } catch (error) {
            toast.error("Failed to process bulk deletion");
        } finally {
            setIsActioning(false);
        }
    };

    const handleToggleBlock = async () => {
        if (!selectedUser) return;
        try {
            setIsActioning(true);
            await toggleBlock({ id: selectedUser._id, reason: blockReason });
            toast.success(`Hellocian ${selectedUser.isBlocked ? 'unblocked' : 'blocked'} successfully`);
            
            // Update local state
            setUsers(users.map(u => u._id === selectedUser._id ? { ...u, isBlocked: !u.isBlocked } : u));
            closeModal();
        } catch (error) {
            toast.error("Failed to update status");
        } finally {
            setIsActioning(false);
        }
    };

    const openModal = (action, user) => {
        setSelectedUser({ ...user, action });
        setBlockReason("");
        setShowModal(true);
        setShowRowDropdown(null);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setBlockReason("");
    };

    const CustomCheckbox = ({ checked, onChange, indeterminate = false }) => (
        <div
            className={`w-5 h-5 border-2 rounded cursor-pointer flex items-center justify-center transition-colors ${checked ? 'bg-[#4B718F] border-[#4B718F]' : 'border-gray-300 dark:border-[#32353c] bg-white dark:bg-[#1d2026]'
                } ${indeterminate ? 'bg-[#4B718F] border-[#4B718F]' : ''}`}
            onClick={onChange}
        >
            {(checked || indeterminate) && (
                <Check className="w-3 h-3 text-white" />
            )}
        </div>
    );

    const StatusBadge = ({ activated, blocked }) => {
        if (blocked) {
            return <span className="px-3 py-1 rounded-full text-sm font-medium border-[1px] border-red-500 dark:border-red-500/50 text-red-500 dark:text-red-400">Blocked</span>;
        }
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${activated
                    ? 'border-[1px] border-[#1FA860] text-[#1FA860] dark:text-green-400 dark:border-green-500/50'
                    : 'border-[1px] border-yellow-500 text-yellow-500 dark:text-yellow-400 dark:border-yellow-500/50'
                }`}>
                {activated ? 'Activated' : 'Pending'}
            </span>
        );
    };

    const selectedCount = users.filter(user => user.selected).length;
    const isIndeterminate = selectedCount > 0 && selectedCount < users.length;
    const allSelected = selectedCount === users.length && users.length > 0;

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-[#32353c] p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#174568]" />
            </div>
        );
    }

    if (users.length === 0 && !isLoading) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-[#32353c]">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c] flex items-center gap-4">
                    <button onClick={() => setView('active')} className={`text-sm font-medium pb-2 ${view === 'active' ? 'text-[#4B718F] border-b-2 border-[#4B718F]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>Active</button>
                    <button onClick={() => setView('trash')} className={`text-sm font-medium pb-2 ${view === 'trash' ? 'text-[#4B718F] border-b-2 border-[#4B718F]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>Trash</button>
                </div>
                <div className="p-8 text-center flex flex-col gap-3">
                    <img src={nohellocian} alt='no hellocian' className='mx-auto w-[131px]' />
                    <p className="text-gray-500 dark:text-gray-400">There are no Hellocians here at the moment.</p>
                    {view === 'active' && <Link to={'/admins/add-new-hellocian'} className="text-xl font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-2">+ Add new</Link>}
                </div>
            </div>
        );
    }

    return (
        <>
            <Link
                to={"/admins/add-new-hellocian"}
                className='mb-3 mr-3 w-[fit-content]  flex justify-end ml-auto border-[1px] bg-[#174568] border-[#174568] text-[15px] text-white rounded py-1 px-3 hover:bg-[#0f3a4f]'
            >
                New Hellocian
            </Link>
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-[#32353c]">
                {/* Header */}

                <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c] flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setView('active')} className={`text-lg font-semibold ${view === 'active' ? 'text-[#4B718F] dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>Active</button>
                        <button onClick={() => setView('trash')} className={`text-lg font-semibold ${view === 'trash' ? 'text-[#4B718F] dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>Trash</button>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setShowHeaderDropdown(!showHeaderDropdown)}
                            className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                        >
                            <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                        </button>

                        {showHeaderDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#191c22] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-10">
                                <button
                                    onClick={selectAllUsers}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                >
                                    <Check className="w-4 h-4" />
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={selectedCount === 0 || isActioning}
                                    className="w-full px-4 py-2 text-left text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete Selected ({selectedCount})
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#1d2026]/60">
                            <tr>
                                <th className="px-6 py-3 text-left">
                                    <CustomCheckbox
                                        checked={allSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={selectAllUsers}
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Active Gigs</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Orders</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider text-nowrap">Date Added</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-[#141414] divide-y divide-gray-200 dark:divide-gray-800">
                            {users.map((user) => (
                                <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                                    <td className="px-6 py-4">
                                        <CustomCheckbox
                                            checked={user.selected}
                                            onChange={() => toggleUserSelection(user._id)}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {user.firstName} {user.lastName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4B718F] dark:text-blue-400">
                                        {user.activeGigCount || 0}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                        {user.orderCount || 0}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <StatusBadge activated={user.isActivated} blocked={user.isBlocked} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="relative">
                                            <button
                                                onClick={() => setShowRowDropdown(showRowDropdown === user._id ? null : user._id)}
                                                className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                                            >
                                                <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                                            </button>

                                            {showRowDropdown === user._id && (
                                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-10">
                                                    {view === 'active' ? (
                                                        <>
                                                            <button
                                                                onClick={() => openModal('view', user)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                View Details
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('message', user)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                                            >
                                                                <MessageCircle className="w-4 h-4" />
                                                                Message
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('block', user)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                                            >
                                                                <Shield className="w-4 h-4" />
                                                                {user.isBlocked ? 'Unblock' : 'Block'}
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('delete', user)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Move to Trash
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => openModal('restore', user)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-green-600 dark:text-green-400"
                                                            >
                                                                <Check className="w-4 h-4" />
                                                                Restore
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('delete', user)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Delete Permanently
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Modal */}
                {showModal && selectedUser && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-[#0b0e14] rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {selectedUser.action === 'view' && 'User Details'}
                                    {selectedUser.action === 'message' && 'Send Message'}
                                    {selectedUser.action === 'block' && 'Block User'}
                                    {selectedUser.action === 'delete' && (view === 'active' ? 'Move to Trash' : 'Delete Permanently')}
                                    {selectedUser.action === 'restore' && 'Restore User'}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="mb-6">
                                 {selectedUser.action === 'view' && (
                                    <div className="space-y-3 dark:text-gray-300">
                                        <p><span className="font-medium text-gray-900 dark:text-white">Name:</span> {selectedUser.firstName} {selectedUser.lastName}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Email:</span> {selectedUser.email}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Skills:</span> {selectedUser.skills?.join(', ') || 'None'}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Phone:</span> {selectedUser.phone}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Status:</span> <StatusBadge activated={selectedUser.isActivated} /></p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Date Added:</span> {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}</p>
                                    </div>
                                )}

                                {selectedUser.action === 'message' && (
                                    <div>
                                        <p className="mb-3 dark:text-gray-300">Send a message to {selectedUser.name}</p>
                                        <textarea
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg resize-none"
                                            rows="4"
                                            placeholder="Type your message here..."
                                        />
                                    </div>
                                )}

                                 {selectedUser.action === 'block' && (
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            Are you sure you want to {selectedUser.isBlocked ? 'unblock' : 'block'} <span className="font-medium text-gray-900 dark:text-white">{selectedUser.firstName}</span>?
                                            {!selectedUser.isBlocked && " They will no longer be able to access the workspace."}
                                        </p>
                                        {!selectedUser.isBlocked && (
                                            <div>
                                                <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Reason (Optional)</label>
                                                <textarea
                                                    className="w-full p-2 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg resize-none text-sm"
                                                    rows="3"
                                                    placeholder="e.g., Violation of terms..."
                                                    value={blockReason}
                                                    onChange={e => setBlockReason(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {selectedUser.action === 'delete' && (
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300">
                                            Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">{selectedUser.firstName}</span>?
                                            {view === 'active' ? ' They will be moved to trash and lose access.' : ' This action is permanent and cannot be undone.'}
                                        </p>
                                    </div>
                                )}

                                {selectedUser.action === 'restore' && (
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300">
                                            Are you sure you want to restore <span className="font-medium text-gray-900 dark:text-white">{selectedUser.firstName}</span>?
                                            They will regain access to their account.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={closeModal}
                                    disabled={isActioning}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (selectedUser.action === 'view') closeModal();
                                        if (selectedUser.action === 'block') handleToggleBlock();
                                        if (selectedUser.action === 'delete') handleDelete(selectedUser._id);
                                        if (selectedUser.action === 'restore') handleRestore(selectedUser._id);
                                    }}
                                    disabled={isActioning}
                                    className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                                        selectedUser.action === 'block' || selectedUser.action === 'delete'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-[#4B718F] hover:bg-[#3a5a75]'
                                        }`}
                                >
                                    {isActioning ? 'Processing...' : (
                                        <>
                                            {selectedUser.action === 'view' && 'Close'}
                                            {selectedUser.action === 'message' && 'Send Message'}
                                            {selectedUser.action === 'block' && (selectedUser.isBlocked ? 'Unblock' : 'Block User')}
                                            {selectedUser.action === 'delete' && (view === 'active' ? 'Move to Trash' : 'Delete Permanently')}
                                            {selectedUser.action === 'restore' && 'Restore User'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Click outside handler */}
                {(showHeaderDropdown || showRowDropdown) && (
                    <div
                        className="fixed inset-0 z-5"
                        onClick={() => {
                            setShowHeaderDropdown(false);
                            setShowRowDropdown(null);
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default HellociansTable;